import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import SectionNoData from "components/Tools/SectionNoData";
import { getEmployeeId } from "./common";

function fmtDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function fmtTime(value) {
  if (!value) return "";
  const parts = String(value).split(":");
  if (parts.length < 2) return "";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
}

function formatRange(ev) {
  if (ev.start_time || ev.end_time) {
    return `${fmtTime(ev.start_time) || "--"} - ${fmtTime(ev.end_time) || "--"}`;
  }
  if (ev.start_date || ev.end_date) {
    return `${fmtDate(ev.start_date) || "--"} - ${fmtDate(ev.end_date) || "--"}`;
  }
  return "--";
}

function ModalAuditEvents(props) {
  const { record } = props;
  const employee = getEmployeeId(record);
  const startDate = record?.audit_start;
  const endDate = record?.audit_end;
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    async function load() {
      setError(null);
      if (!employee || !startDate || !endDate) {
        setEvents([]);
        return;
      }
      const { data, error } = await proxy.methodCall({
        model: "staff.shift",
        method: "get_events_by_employee",
        args: [[employee], startDate, endDate],
      });
      if (!active) return;
      if (error) {
        setEvents([]);
        setError(typeof error === "string" ? error : error?.message);
        return;
      }
      setEvents(data?.[String(employee)] || []);
    }
    load();
    return () => {
      active = false;
    };
  }, [employee, startDate, endDate]);

  return (
    <div className="flex flex-col w-full bg-neutral-100 min-h-[30vh]">
      {error && (
        <p className="text-rose-500 text-sm mb-2">
          <FM id={error} defaultMessage={error} />
        </p>
      )}
      {events === null ? (
        <SectionNoData text="..." />
      ) : events.length === 0 ? (
        <SectionNoData text={<FM id="staff.access.audit.events_empty" />} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-neutral-100 text-neutral-600 text-xs uppercase tracking-wide">
                <th className="text-left px-3 py-2">
                  <FM id="staff.access.audit.events_category" />
                </th>
                <th className="text-left px-3 py-2">
                  <FM id="staff.access.audit.events_description" />
                </th>
                <th className="text-left px-3 py-2">
                  <FM id="staff.access.audit.events_date" />
                </th>
                <th className="text-left px-3 py-2">
                  <FM id="staff.access.audit.events_range" />
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, index) => (
                <tr
                  key={ev.id ?? index}
                  className="border-t border-neutral-100 hover:bg-amber-50/50"
                >
                  <td className="px-3 py-2 font-medium text-neutral-700">
                    {ev.category_name || "--"}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {ev.description || "--"}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {fmtDate(ev.event_date || ev.start_date) || "--"}
                  </td>
                  <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">
                    {formatRange(ev)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ModalAuditEvents;